import { useTranslation } from 'react-i18next';

import DownFill from '@/assets/icons/down_fill.svg?component';
import UpFill from '@/assets/icons/up_fill.svg?component';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { confirm } from '@/components/molecules/ConfirmDialog';
import { ToastError } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { translateDataLabel } from '@/i18n/dataLabels';

import type { ClientCageProps, ClientLocationProps } from '../Client';

import type { LocationsTableHandlers } from './locationsTableTypes';

import styles from './LocationsTable.module.scss';

export function LocationActions(props: {
    location: ClientLocationProps;
    handlers: LocationsTableHandlers;
}) {
    const { location, handlers } = props;
    const { t } = useTranslation();
    return (
        <div className={styles.actionButtonContainer}>
            <Button
                variant={'secondary'}
                onClick={(e) => {
                    handlers.onUpdateLocation(location);
                    e.stopPropagation();
                }}>
                {t('common.actions.edit')}
            </Button>
            <Button
                variant={'secondary'}
                onClick={(e) => {
                    handlers.onAddCageToLocation(location.id);
                    e.stopPropagation();
                }}>
                {t('internal.client.addCage')}
            </Button>
            <Button
                variant={'danger'}
                onClick={(e) => {
                    handlers.onDeleteCageToLocation(location.id);
                    e.stopPropagation();
                }}>
                {t('common.actions.deleteEntry')}
            </Button>
        </div>
    );
}

export function CageActions(props: {
    cage: ClientCageProps;
    location: ClientLocationProps;
    handlers: LocationsTableHandlers;
}) {
    const { cage, location, handlers } = props;
    const { t } = useTranslation();

    // The buttons fire these and move on, so a failure is told here rather than rejected.
    const run = async (action: () => Promise<unknown>): Promise<void> => {
        try {
            await action();
        } catch (error: unknown) {
            ToastError(
                apiErrorContent(error, t('internal.clientLocations.cageActionFailed')),
                'client-cage-action-error'
            );
        }
    };

    return (
        <div className={styles.expandedButtonsContainer}>
            <Button variant={'secondary'} onClick={() => handlers.onEditCage(cage, location)}>
                {t('common.actions.edit')}
            </Button>
            <Button
                variant={'secondary'}
                onClick={() => run(() => cage.markHistorical(!cage.historical))}>
                {cage.historical
                    ? t('internal.clientLocations.removeHistorical')
                    : t('internal.clientLocations.markHistorical')}
            </Button>
            <Button
                onClick={() =>
                    confirm({
                        title: t('internal.clientLocations.deleteCageTitle'),
                        message: t('internal.clientLocations.confirmDeleteCage'),
                        confirmLabel: t('common.actions.delete'),
                        errorFallback: t('internal.clientLocations.cageActionFailed'),
                        onConfirm: () => handlers.deleteCage(cage.id),
                    })
                }
                variant={'danger'}>
                {t('common.actions.deleteEntry')}
            </Button>
        </div>
    );
}

export function ExpandToggle(props: {
    location: ClientLocationProps;
    handlers: LocationsTableHandlers;
}) {
    const { location, handlers } = props;
    const { t } = useTranslation();
    if (location.cages.length === 0) return null;
    const expanded = handlers.isExpanded(location.id);

    // A button, so the pens can be shown from the keyboard as well.
    return (
        <Button
            variant="ghost"
            size="small"
            iconOnly
            aria-label={t('clientLocations.locationsTable.showPens', { name: location.name })}
            aria-expanded={expanded}
            onClick={() => handlers.onToggleExpanded(location.id)}
            icon={
                <SvgIcon
                    icon={expanded ? UpFill : DownFill}
                    size="inherit"
                    svgProps={{ 'aria-hidden': true }}
                />
            }
        />
    );
}

export function LocationSpecies(props: { location: ClientLocationProps }) {
    const { location } = props;
    const { t } = useTranslation();
    if (!location.fishType) return <>-</>;
    return (
        <>
            {translateDataLabel(t, 'fishTypes', location.fishType.name)}{' '}
            <span className={styles.hint}>({t('internal.clientLocations.default')})</span>
        </>
    );
}
